import { FormEvent, HTMLAttributes, useEffect, useRef, useState } from 'react'
import { useKey } from 'react-use'
import { useGSAP } from '@gsap/react'
import gsap from 'gsap'
import Link from 'next/link'
import { useRouter } from 'next/router'

import SVGIcon from '@/components/base/svg-icon'
import Typography from '@/components/base/typography'
import { useStore } from '@/contexts/store'
import useStableViewport from '@/hooks/use-stable-viewport'
import { useLayoutStore } from '@/store/layout'

import { cn } from '@/lib/utils'
import { WPMenu } from '@/types'

const Menu = ({ className, ...props }: HTMLAttributes<HTMLDivElement>) => {
  const { isMobile } = useStableViewport()
  const rootRef = useRef<HTMLDivElement>(null!)
  const mobileMenuRef = useRef<HTMLDivElement>(null!)
  const inputRef = useRef<HTMLInputElement>(null!)
  const { menu, strings } = useStore()
  const [viewSubmenu, setViewSubmenu] = useState(false)
  const [activeMenu, setActiveMenu] = useState<WPMenu | null>(null)
  const menuOpen = useLayoutStore((state) => state.menuOpen)
  const [query, setQuery] = useState('')
  const router = useRouter()

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault()
    const trimmed = query.trim()
    if (!trimmed) return

    router.push({
      pathname: '/search',
      query: { q: trimmed }
    })
  }

  useEffect(() => {
    if (!menuOpen) {
      gsap.delayedCall(0.5, () => {
        setViewSubmenu(false)
      })
      setQuery('')
      if (inputRef.current) {
        inputRef.current.value = ''
      }
    }
  }, [menuOpen])

  useKey(
    'Tab',
    (e) => {
      if (!menuOpen || !rootRef.current) return

      const focusable = rootRef.current.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )
      if (!focusable.length) return

      const first = focusable[0]
      const last = focusable[focusable.length - 1]

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault()
        last.focus()
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault()
        first.focus()
      }
    },
    { event: 'keydown' },
    [menuOpen]
  )

  useGSAP(
    () => {
      gsap.to(mobileMenuRef.current, {
        x: viewSubmenu ? '-50%' : '0%',
        duration: 0.5,
        ease: 'power3.inOut'
      })
    },
    {
      scope: mobileMenuRef,
      dependencies: [viewSubmenu]
    }
  )

  useGSAP(
    () => {
      const mm = gsap.matchMedia()

      gsap.to(rootRef.current, {
        pointerEvents: menuOpen ? 'all' : 'none',
        opacity: menuOpen ? 1 : 0,
        duration: 0.5,
        ease: 'power3.inOut'
      })

      mm.add(
        {
          small: '(max-width: 767px)',
          large: '(min-width: 768px)'
        },
        (ctx) => {
          const small = ctx.conditions?.small
          const q = gsap.utils.selector(rootRef)

          const nav = small ? '[data-mobile-menu]' : '[data-desktop-menu]'
          const elements = [
            ...q(`${nav} li, ${nav} p`),
            ...q('[data-social-menu] li'),
            ...q('form')
          ]

          gsap.to(elements, {
            y: menuOpen ? 0 : -20,
            opacity: menuOpen ? 1 : 0,
            duration: menuOpen ? 0.8 : 0,
            ease: 'power3.inOut',
            delay: 0.2,
            stagger: {
              each: 0.02,
              from: 'start'
            }
          })
        }
      )
    },
    {
      scope: rootRef,
      dependencies: [menuOpen]
    }
  )

  const socialMenu = () => (
    <nav aria-label={strings?.labels?.social_navigation || 'Social'}>
      <ul className="flex gap-spacing-sm">
        {menu?.social_menu?.menu?.map((item, index) => (
          <li key={`${item.id}-${index}`}>
            <Link
              className="group flex"
              href={item.url || '#'}
              target={item.target || '_self'}
              rel="noopener noreferrer"
              scroll={false}
              aria-label={item.title ? `Seguici su ${item.title}` : undefined}
            >
              <SVGIcon
                symbol={item.title.toLowerCase() || 'facebook'}
                className="size-[24px] transition-opacity duration-300 group-hover:opacity-50"
              />
            </Link>
          </li>
        ))}
      </ul>
    </nav>
  )

  const menu01 = () => {
    const menu01 = isMobile
      ? menu?.mobile_menu_01?.menu || menu?.main_menu_01?.menu
      : menu?.main_menu_01?.menu
    return (
      <nav
        className={cn('flex')}
        aria-label={
          strings?.labels?.main_navigation || 'Navigazione principale'
        }
      >
        <ul className="flex flex-col gap-spacing-sm">
          {menu01?.map((item, index) => (
            <li key={`${item.id}-${index}`}>
              <Link
                className="group flex"
                href={item.url || '#'}
                target={item.target || '_self'}
                scroll={false}
              >
                <Typography
                  variant={item.classes?.includes('all') ? 'large' : 'h3'}
                  className={cn(
                    item.classes?.includes('all') && 'condensed pt-spacing-2xs',
                    'transition-opacity duration-300 group-hover:opacity-50'
                  )}
                >
                  {item.title}
                </Typography>
              </Link>
            </li>
          ))}
        </ul>
      </nav>
    )
  }

  const desktopMenu = () => (
    <div
      className="hidden w-full flex-grow items-center lg:flex"
      data-desktop-menu
    >
      <div className="flex w-full justify-between">
        <div className="w-[20vw]">{menu01()}</div>

        <div className="w-[20vw]">
          <nav
            className={cn('flex flex-col gap-spacing-sm')}
            aria-label={menu?.main_menu_02?.acf?.title || 'Collections'}
          >
            <Typography variant="small" className="condensed" component="p">
              {menu?.main_menu_02?.acf?.title || 'Collections'}
            </Typography>
            <ul className="flex flex-col gap-spacing-xs">
              {menu?.main_menu_02?.menu?.map((item, index) => (
                <li key={`${item.id}-${index}`}>
                  <Link
                    className="group flex"
                    href={item.url || '#'}
                    target={item.target || '_self'}
                    scroll={false}
                  >
                    <Typography
                      variant={item.classes?.includes('all') ? 'large' : 'h4'}
                      className={cn(
                        item.classes?.includes('all') &&
                          'condensed pt-spacing-2xs',
                        'transition-opacity duration-300 group-hover:opacity-50'
                      )}
                    >
                      {item.title}
                    </Typography>
                  </Link>
                </li>
              ))}
            </ul>
          </nav>
        </div>

        <div className="flex w-[20vw] flex-col gap-spacing-lg">
          <nav
            className={cn('flex flex-col', 'gap-spacing-sm')}
            aria-label={menu?.main_menu_03?.acf?.title || 'Professionals'}
          >
            <Typography variant="small" className="condensed" component="p">
              {menu?.main_menu_03?.acf?.title || 'Professionals'}
            </Typography>
            <ul className="flex flex-col gap-spacing-xs">
              {menu?.main_menu_03?.menu?.map((item, index) => (
                <li key={`${item.id}-${index}`}>
                  <Link
                    href={item.url || '#'}
                    target={item.target || '_self'}
                    className="group flex"
                    scroll={false}
                  >
                    <Typography
                      variant="large"
                      className="transition-opacity duration-300 group-hover:opacity-50"
                    >
                      {item.title}
                    </Typography>
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

          <nav
            className={cn('flex flex-col', 'gap-spacing-sm')}
            aria-label={menu?.main_menu_04?.acf?.title || 'Company'}
          >
            <Typography variant="small" className="condensed" component="p">
              {menu?.main_menu_04?.acf?.title || 'Company'}
            </Typography>
            <ul className="flex flex-col gap-spacing-xs">
              {menu?.main_menu_04?.menu?.map((item, index) => (
                <li key={`${item.id}-${index}`}>
                  <Link
                    className="group flex"
                    href={item.url || '#'}
                    target={item.target || '_self'}
                    scroll={false}
                  >
                    <Typography
                      variant="large"
                      className="transition-opacity duration-300 group-hover:opacity-50"
                    >
                      {item.title}
                    </Typography>
                  </Link>
                </li>
              ))}
            </ul>
          </nav>
        </div>
      </div>
    </div>
  )

  const mobileMenu = () => {
    const menu02 = isMobile
      ? menu?.mobile_menu_02 || menu?.main_menu_02
      : menu?.main_menu_02
    const menu03 = isMobile
      ? menu?.mobile_menu_03 || menu?.main_menu_03
      : menu?.main_menu_03
    const menu04 = isMobile
      ? menu?.mobile_menu_04 || menu?.main_menu_04
      : menu?.main_menu_04

    return (
      <div
        className={cn(
          'flex min-h-0 w-[200%] flex-grow overflow-y-auto pt-[24px] lg:hidden'
        )}
        ref={mobileMenuRef}
        data-mobile-menu
      >
        <div
          className="flex min-h-fit w-1/2 shrink-0 flex-col justify-center"
          data-mobile-menu-1
        >
          <div className="mb-[24px] flex w-full flex-col gap-spacing-md">
            {menu01()}
          </div>
          <nav
            className={cn('flex w-full flex-col gap-spacing-xs pb-spacing-lg')}
            aria-label={strings?.labels?.menu || 'Menu'}
          >
            {/* <Typography variant="small" className="condensed" component="p">
            {strings?.labels?.menu}
          </Typography> */}
            <ul className="flex flex-col">
              {[menu02, menu03, menu04].map((item, index) => (
                <li
                  key={`${item?.acf?.title || 'menu'}-${index}`}
                  className="block w-full"
                >
                  <button
                    className="group flex w-full items-center border-b-[1px] border-b-border-secondary-light py-spacing-xs text-left dark:border-b-border-secondary-dark"
                    onClick={() => {
                      setViewSubmenu(true)
                      setActiveMenu(item as WPMenu)
                    }}
                    aria-expanded={viewSubmenu && activeMenu === item}
                    aria-label={`${strings?.labels?.submenu_open || 'Apri sottomenu'} ${item?.acf?.title}`}
                  >
                    <Typography
                      variant="large"
                      className="block transition-opacity duration-300 group-hover:opacity-50"
                    >
                      {item?.acf?.title}
                    </Typography>
                    <SVGIcon
                      symbol="cta-arrow"
                      className="ml-auto size-[16px] rotate-45"
                    />
                  </button>
                </li>
              ))}
              {strings?.menu?.label && strings?.menu?.link?.url && (
                <li className="block w-full">
                  <Link
                    href={strings.menu.link.url}
                    target={strings.menu.link.target || '_self'}
                    scroll={false}
                    className="group flex w-full items-center border-b-[1px] border-b-border-secondary-light py-spacing-xs text-left dark:border-b-border-secondary-dark"
                  >
                    <Typography
                      variant="large"
                      className="block transition-opacity duration-300 group-hover:opacity-50"
                    >
                      {strings.menu.label}
                    </Typography>
                    <SVGIcon
                      symbol="cta-arrow"
                      className="ml-auto size-[16px] rotate-45"
                    />
                  </Link>
                </li>
              )}
            </ul>
          </nav>
          <div>
            <form onSubmit={handleSubmit} role="search" className="w-full">
              <div className="relative w-full">
                <label htmlFor="search-input" className="sr-only">
                  {strings?.labels?.search_placeholder || 'Cerca nel sito'}
                </label>
                <input
                  ref={inputRef}
                  id="search-input"
                  type="search"
                  name="q"
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder={
                    strings?.labels?.search_placeholder || 'Cerca nel sito...'
                  }
                  defaultValue={query}
                  autoComplete="off"
                  className={cn(
                    'text-large w-full border-b bg-transparent py-spacing-2xs pr-[48px] font-helvetica font-light text-text-heading-invert-dark outline-none dark:text-text-heading-invert-light',
                    'border-b-border-secondary-light dark:border-b-border-secondary-dark',
                    'placeholder:text-text-heading-invert-dark/40 dark:placeholder:text-text-heading-invert-light/40',
                    'focus:border-b-text-heading-invert-dark dark:focus:border-b-text-heading-invert-light',
                    'transition-colors duration-300'
                  )}
                />
                <button
                  type="submit"
                  aria-label={strings?.labels?.submit_search || 'Avvia ricerca'}
                  className="absolute right-0 top-1/2 flex size-[48px] -translate-y-1/2 items-center justify-center text-text-heading-invert-dark transition-opacity hover:opacity-70 dark:text-text-heading-invert-light"
                >
                  <SVGIcon
                    symbol="search"
                    className="size-[24px] fill-current"
                  />
                </button>
              </div>
            </form>
          </div>
        </div>
        <div
          className="flex h-full w-1/2 shrink-0 flex-col justify-center"
          data-mobile-menu-2
        >
          <button
            onClick={() => {
              setViewSubmenu(false)
            }}
            className="mb-[40px] place-self-start"
            aria-label={
              strings?.labels?.submenu_back || 'Torna al menu principale'
            }
          >
            <SVGIcon
              symbol="cta-arrow"
              className="ml-auto size-[16px] rotate-[-135deg]"
            />
          </button>
          <nav
            className={cn('flex w-full flex-col gap-spacing-md')}
            aria-label={activeMenu?.acf?.title || 'Sottomenu'}
          >
            <Typography variant="small" className="condensed">
              {activeMenu?.acf?.title}
            </Typography>
            <ul className="flex flex-col gap-spacing-sm">
              {activeMenu?.menu?.map((item, index) => (
                <li key={`${item?.id}-${index}`} className="block w-full">
                  <Link
                    href={item.url || '#'}
                    className="group flex w-full"
                    scroll={false}
                  >
                    <Typography
                      variant={item.classes?.includes('all') ? 'large' : 'h3'}
                      className={cn(
                        item.classes?.includes('all') &&
                          'condensed pt-spacing-2xs',
                        'transition-opacity duration-300 group-hover:opacity-50'
                      )}
                    >
                      {item.title}
                    </Typography>
                  </Link>
                </li>
              ))}
            </ul>
          </nav>
        </div>
      </div>
    )
  }

  return (
    <div
      ref={rootRef}
      id="main-menu"
      role="dialog"
      aria-modal="true"
      aria-label={strings?.labels?.main_navigation || 'Navigazione principale'}
      aria-hidden={!menuOpen}
      {...(!menuOpen ? { inert: true } : {})}
      className={cn(
        'fixed z-menu',
        'inset-0 bg-surface-primary-dark pt-header-height dark:bg-surface-primary-light',
        'px-padding-sm pb-spacing-sm lg:px-padding-lg lg:pb-spacing-2xl',
        'pointer-events-none opacity-0',
        className
      )}
      {...props}
      data-menu
    >
      <div className="flex h-full w-full flex-col overflow-hidden">
        {desktopMenu()}
        {mobileMenu()}
        <div
          className="flex justify-start pt-[15px] lg:justify-end lg:pt-0"
          data-social-menu
        >
          {socialMenu()}
        </div>
      </div>
    </div>
  )
}

export default Menu
